import { useId } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

type LineSeg = { x1: number; y1: number; x2: number; y2: number };

/** Straight connector (Gantt-style dependency), not a Bezier — reads clearly on dense tables. */
function straightPath({ x1, y1, x2, y2 }: LineSeg): string {
  return `M ${x1} ${y1} L ${x2} ${y2}`;
}

/**
 * Full-viewport SVG overlay: straight line from source row to linked row with arrow (scheduling-tool convention).
 */
export function ScheduleDependencyJumpOverlay({ line }: { line: LineSeg | null }) {
  const gid = useId().replace(/:/g, '');
  const arrowId = `dep-jump-arrow-${gid}`;
  const draw = { duration: 0.35, ease: [0.22, 1, 0.36, 1] as const };

  return (
    <AnimatePresence>
      {line ? (
        <motion.svg
          key="dep-jump"
          className="pointer-events-none fixed inset-0 z-[60] h-full w-full"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          aria-hidden
        >
          <defs>
            <marker
              id={arrowId}
              markerWidth="9"
              markerHeight="9"
              refX="8"
              refY="3"
              orient="auto"
              markerUnits="userSpaceOnUse"
            >
              <path d="M0,0 L8,3 L0,6 Z" fill="#0f766e" />
            </marker>
          </defs>
          {/* Light underlay so the line stays readable on striped / coloured rows */}
          <motion.path
            d={straightPath(line)}
            fill="none"
            stroke="rgba(255,255,255,0.95)"
            strokeWidth={5}
            strokeLinecap="round"
            strokeLinejoin="round"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ pathLength: draw }}
          />
          <motion.path
            d={straightPath(line)}
            fill="none"
            stroke="#0d9488"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            markerEnd={`url(#${arrowId})`}
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ pathLength: draw }}
          />
        </motion.svg>
      ) : null}
    </AnimatePresence>
  );
}
